import React, { FormEvent } from "react"
import {
    ModalPanel,
    Title,
    SingleLineTextInput,
    Button,
    MultiLineTextInput
} from "dash-styled"
import { useDispatch } from "react-redux"
import { useAppState } from "../../../Hooks/useReduxState"
import { getEmail } from "../../../Helpers/Functions/Selectors/auth"
import { sendContactUsMessage } from "../../../Actions/API/ContactActions"
import {
    ContactUsMessageModel,
    ContactUsMessageExtraDataModel
} from "../../../Models/External/FeedbackModel"
import { useInput } from "../../../Hooks/useInput"
import { useBoolean } from "../../../Hooks/useBoolean"
import { MainThunkDispatch } from "../../../Reducers"

interface Props {
    title?: string
    prefilledMessage?: string
    extraData: ContactUsMessageExtraDataModel
    onClose: () => void
}

interface IsolatedProps {
    submitting: boolean
    title?: string
    prefilledMessage?: string
    prefilledEmail?: string
    onSubmit: (data: Omit<ContactUsMessageModel, "extraData">) => void
    onClose: () => void
    onCancel?: () => void
}

function ContactFormIsolated({
    title,
    prefilledMessage,
    prefilledEmail,
    onSubmit,
    onClose,
    onCancel,
    submitting
}: IsolatedProps) {
    const email = useInput(prefilledEmail || "", prefilledEmail !== undefined)
    const phone = useInput("", true)
    const company = useInput("", true)
    const message = useInput(
        prefilledMessage || "",
        prefilledMessage !== undefined
    )
    const isValid =
        email.isValid &&
        email.value !== "" &&
        phone.isValid &&
        company.isValid &&
        message.isValid
    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        onSubmit({
            title,
            email: email.value,
            phone: phone.value,
            company: company.value,
            message: message.value
        })
    }

    return (
        <ModalPanel
            testId="contact-form"
            className="max-w-2xl w-full"
            onClose={onClose}
        >
            <Title variant="heading2" weight="semibold">
                Contact us
            </Title>
            <form className="p-6 pb-0" onSubmit={handleSubmit}>
                <SingleLineTextInput
                    testId="email-input"
                    className="mb-1"
                    type="email"
                    label="Your email"
                    placeholder="e.g. your_email@company.com"
                    value={email.value}
                    onChange={email.onChange}
                />
                <div className="flex mb-1">
                    <SingleLineTextInput
                        testId="phone-input"
                        className="mr-3"
                        label="Phone number"
                        placeholder="e.g. +49 1555 333 4444"
                        value={phone.value}
                        onChange={phone.onChange}
                    />
                    <SingleLineTextInput
                        testId="company-input"
                        label="Company"
                        value={company.value}
                        onChange={company.onChange}
                    />
                </div>
                <MultiLineTextInput
                    testId="message-input"
                    variant="bordered"
                    label="Message"
                    inputProps={{
                        value: message.value,
                        rows: 5,
                        onChange: message.onChange
                    }}
                />
                <div className="flex justify-center pt-8">
                    <Button
                        testId="submit-contact-button"
                        type="submit"
                        color="darkviolet"
                        className="w-48 mr-4"
                        disabled={!isValid || submitting}
                    >
                        {submitting ? "Submitting..." : "Submit"}
                    </Button>
                    {onCancel && (
                        <Button
                            color="plain"
                            className="w-48"
                            onClick={onClose}
                        >
                            Cancel
                        </Button>
                    )}
                </div>
            </form>
        </ModalPanel>
    )
}

export function ContactForm({
    prefilledMessage,
    extraData,
    onClose,
    title
}: Props) {
    const dispatch = useDispatch<MainThunkDispatch>()
    const email = useAppState(getEmail)
    const [isSubmitting, setSubmitting, endSubmitting] = useBoolean(false)
    const handleSubmit = (data: Omit<ContactUsMessageModel, "extraData">) => {
        setSubmitting()
        dispatch(sendContactUsMessage({ ...data, extraData })).then(() => {
            endSubmitting()
            onClose()
        })
    }

    return (
        <ContactFormIsolated
            prefilledMessage={prefilledMessage}
            prefilledEmail={email}
            onSubmit={handleSubmit}
            onClose={onClose}
            title={title}
            submitting={isSubmitting}
        />
    )
}
